/**
 * `diffForAction` — the old → new field diff shown on an update confirm card.
 *
 * This is a core *trust mechanic*: before the operator confirms a write that
 * mutates an existing record, the card shows exactly which field changes and
 * from what to what — not just the new value. Creates have nothing to diff and
 * return null (the card already shows the full new record).
 *
 * Pulled out of ChatView into its own pure module so the mapping can be locked
 * with unit tests (no React, no store) — the same reason the producer agents
 * export their pure mappings. ChatView imports this and renders the result on
 * each pending update card; the function reads the live `people` snapshot for
 * contact-field diffs and reads everything else straight off the action.
 *
 * IMPORTANT: every action kind that mutates an *existing* record should have a
 * case here. A missing case falls through to `null`, which silently drops the
 * old→new line and weakens the confirm card — that was the `assign_ticket`
 * bug this module was created to close (an owner reassignment is an update and
 * must show `owner: <from> → <to>`).
 */

import type { AgentAction } from './types';
import type { BrainFact, Person } from '../db/types';

/**
 * Old → new field diff for actions that mutate an existing record, read
 * against the live store snapshot at render time. Creates return null —
 * there's nothing to diff. This is what makes update cards trustworthy:
 * you see exactly what changes, not just the new value.
 *
 * `brainFacts` is the flat all-accounts `brainFacts` head; it's optional and
 * defaults to empty so callers that never propose `update_brain` (and the
 * pure unit tests) don't have to thread a snapshot they don't need.
 */
export function diffForAction(
  action: AgentAction,
  people: Person[],
  brainFacts: BrainFact[] = [],
): string | null {
  switch (action.kind) {
    case 'set_prospect_stage': {
      const p = people.find((x) => x.id === action.personId);
      return p?.stage && p.stage !== action.stage
        ? `stage: ${p.stage} → ${action.stage}`
        : null;
    }
    case 'set_customer_status': {
      const p = people.find((x) => x.id === action.personId);
      return p?.status && p.status !== action.status
        ? `status: ${p.status} → ${action.status}`
        : null;
    }
    case 'schedule_follow_up': {
      const p = people.find((x) => x.id === action.personId);
      const to = action.followUpDate ?? 'in 3 days';
      return p?.followUpDate && p.followUpDate !== to
        ? `follow-up: ${p.followUpDate} → ${to}`
        : null;
    }
    case 'move_workflow_stage':
      return `${action.workflowName}: ${action.fromStage} → ${action.toStage}`;
    case 'set_ticket_status':
      return `status → ${action.status}`;
    case 'assign_ticket': {
      // Reassigning a ticket's owner is an update — show the handoff. The
      // prior owner rides along on the action (`fromOwner`, null when the
      // ticket was unowned), so the diff reads straight off the action with
      // no tickets snapshot needed. A no-op reassignment (same owner) has
      // nothing to show.
      const from = action.fromOwner;
      if (from && from === action.owner) return null;
      return from ? `owner: ${from} → ${action.owner}` : `owner → ${action.owner}`;
    }
    case 'update_brain': {
      // Writing a Brain fact the account already holds is an update — show
      // the operator exactly what value they're overwriting. The head is
      // keyed by the same deterministic id the write path uses
      // (`${accountId}:${section}:${field}`), so the current value reads
      // straight off the snapshot. A brand-new field (no existing fact) has
      // nothing to diff — the card already shows the full new fact — and a
      // no-op write (same value) shows nothing, mirroring the contact-field
      // and assign_ticket cases above.
      const id = `${action.accountId}:${action.section}:${action.field}`;
      const existing = brainFacts.find((f) => f.id === id);
      return existing && existing.value !== action.value
        ? `${action.label}: ${existing.value} → ${action.value}`
        : null;
    }
    case 'update_record_field': {
      // A governed inline cell edit is always an update — the prior value
      // rides on the action (`from`, null when the field was empty), so the
      // diff reads straight off it. A no-op edit (same value) shows nothing.
      if (action.from !== null && action.from === action.to) return null;
      return action.from !== null
        ? `${action.attr}: ${action.from} → ${action.to}`
        : `${action.attr} → ${action.to}`;
    }
    default:
      return null;
  }
}
